import React from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { Loyalty } from 'components/app-header/loyalty';
import styleLoadingIndicator from 'components/app-header/style-loading-indicator';
import defaultConfig from 'components/app-header/styled';
import { AppSubheader } from 'components/app-subheader';
import Currency from 'components/currency';
import { DeliveryBannerContainer } from 'components/delivery-banner';
import { Drawer } from 'components/drawer';
import IconProfileTH from 'components/icons/profile-th';
import LoadingAnimation from 'components/loading-animation';
import Logo from 'components/logo';
import SkipToContent from 'components/skip-content';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useLocalizedRoutesForServiceModeHeaderDisplay from 'hooks/use-localized-routes-for-service-mode-header-display';
import useMediaQuery from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingIndicator } from 'state/loading';
import { usePaymentContext } from 'state/payment';
import { IPaymentMethod } from 'state/payment/types';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { delayUnmountHOC } from 'utils/delay-unmount-hoc';
import { routes } from 'utils/routing';

import { StyledCenterSpacer } from '../styled/base';
import { StyledLogo } from '../styled/mobile-header';

import { IMobileHeaderProps } from './types';

const MobileHeaderContainer = styled.div`
  position: fixed;
  width: 100%;
  z-index: ${Styles.zIndex.top};
  top: 0;
  ${Styles.desktop`
    display: none;
  `}
`;

const Header = styled.div`
  align-items: center;
  background: ${p => p.theme.token('top-nav-bar-background-color')};
  color: ${Styles.color.white};
  display: flex;
  height: ${Styles.layout.navHeight.mobile};
  justify-content: space-between;
  padding: 2rem 1rem 1.5rem;
  ${Styles.mobileFullscreen`
    padding-top: calc(env(safe-area-inset-top) + 2rem);
  `}
  text-align: center;
  width: 100%;
  position: relative;
`;

const MobileItemContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  width: 33.33%; // fallback for flex; fixes display bug
  &.last {
    padding-right: env(safe-area-inset-right);
    justify-content: flex-end;
  }
`;

const LinkNoDecoration = styled(Link)`
  ::before {
    color: transparent;
  }
  &:active,
  &:hover,
  &:link,
  &:visited {
    color: inherit;
  }
`;

const StyledCenterSpacerTH = styled(StyledCenterSpacer)`
  left: 50%;
  top: 50%;
  padding-top: env(safe-area-inset-top);
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: ${Styles.zIndex.max - 300};
`;

const StyledProfile = styled(IconProfileTH)`
  height: 24px;
  fill: ${Styles.color.white};
`;

const PaddedText = styled.span`
  margin-bottom: 3px;
`;
const DelayedUnmountDrawer = delayUnmountHOC(Drawer);
const MobileHeader = React.forwardRef<HTMLDivElement, IMobileHeaderProps>(
  ({ isAuthenticated, serviceMode, userAddress, cateringPickupDateTime }, ref) => {
    const LoadingIndicator = useLoadingIndicator();
    const { formatMessage } = useIntl();
    const { isStoreOpenAndAvailable } = useStoreContext();
    const {
      getBalanceFromPaymentMethods,
      paymentMethods,
      loading: paymentLoading,
    } = usePaymentContext();
    const { getLocalizedRouteForPath } = useRoutes();
    const { showCardTab, showRewardsTab, hideDrawer, isShowingDrawer } = useUIContext();
    const { loading: loyaltyLoading, loyaltyPointsFraction } = useLoyaltyActiveCard();
    // determines whether service mode is shown using current window location and service mode
    const showServiceMode = useLocalizedRoutesForServiceModeHeaderDisplay(serviceMode);
    const enableAccountInFooter = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_IN_FOOTER);
    const StyledLoadingIndicator = styleLoadingIndicator(LoadingIndicator);

    let additionalButtons;
    const isMobile = useMediaQuery('mobile');

    if (isAuthenticated() && !isMobile) {
      if (paymentLoading || loyaltyLoading) {
        additionalButtons = <LoadingAnimation />;
      } else {
        const prepaidPaymentMethod = paymentMethods.find(
          (method: IPaymentMethod) => (method.prepaid as unknown) as boolean
        );
        let balanceAmount = 0;
        if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
          balanceAmount = getBalanceFromPaymentMethods(prepaidPaymentMethod!);
        }
        additionalButtons = (
          <>
            <ActionButton
              variant={defaultConfig.linkVariant}
              transparent
              onClick={showRewardsTab}
              data-testid="my-rewards-link"
              title={formatMessage({ id: 'myRewards' })}
            >
              <PaddedText>{formatMessage({ id: 'visits' })}</PaddedText>
              <span>{loyaltyPointsFraction}</span>
            </ActionButton>
            <ActionButton
              variant={defaultConfig.linkVariant}
              transparent
              onClick={showCardTab}
              data-testid="tim-card-link"
              title="Tim Card"
            >
              <PaddedText>{formatMessage({ id: 'thTimsRewards' })}</PaddedText>
              <Currency amount={balanceAmount} />
            </ActionButton>
          </>
        );
      }
    }

    const headerLogo = (
      <LinkNoDecoration to={routes.base}>
        <StyledLogo>
          <Logo />
        </StyledLogo>
      </LinkNoDecoration>
    );

    return (
      <MobileHeaderContainer aria-label={formatMessage({ id: 'primaryNavLinks' })} ref={ref}>
        <Header>
          <SkipToContent />
          <StyledCenterSpacerTH $center={false}>{headerLogo}</StyledCenterSpacerTH>
          <MobileItemContainer className="last">
            {additionalButtons}
            {isAuthenticated() ? (
              !enableAccountInFooter && (
                <LinkNoDecoration
                  data-testid="mobile-nav-account-link"
                  to={getLocalizedRouteForPath(routes.account)}
                >
                  <StyledProfile />
                </LinkNoDecoration>
              )
            ) : (
              <Box>
                <ActionButton
                  variant={ActionButtonVariants.INVERSE}
                  size={ActionButtonSizes.SMALL}
                  to={routes.signIn}
                  data-testid="mobile-nav-sign-in-link"
                >
                  {formatMessage({ id: 'logIn' })}
                </ActionButton>
              </Box>
            )}
          </MobileItemContainer>
          <DelayedUnmountDrawer
            onDismiss={hideDrawer}
            delayTime={500}
            isVisible={isShowingDrawer}
            disableScroll
          >
            <Loyalty onDismiss={hideDrawer} />
          </DelayedUnmountDrawer>
          <StyledLoadingIndicator hasStoreSelection={isStoreOpenAndAvailable} />
        </Header>
        <DeliveryBannerContainer />
        {showServiceMode && (
          <AppSubheader
            serviceMode={serviceMode!}
            userAddress={userAddress}
            cateringPickupDateTime={cateringPickupDateTime}
          />
        )}
      </MobileHeaderContainer>
    );
  }
);

export default MobileHeader;
